import React, { useEffect, useRef, useState } from "react";

/**
 * AlarmRinger — full-screen takeover overlay that fires when an alarm
 * `pending_alarm` flag comes back from the backend poll. It does THREE
 * things at once:
 *   1. Plays a looping alarm tone via Web Audio API (no asset needed)
 *   2. Shows a big black-and-gold "ALARM" panel with the label + time
 *   3. Speaks "GOOD MORNING. It's [time]. Time to get up." via the
 *      provided onSpeak callback
 *
 * Owner asked: "if my screen is left on I need this widget to start
 * ringing an alarm clock noise whatever time I say to through my
 * computer speakers". The tone is a classic two-pitch warble (650Hz
 * alternating with 920Hz at 0.5s) — sounds like an old clock radio.
 *
 * Dismiss with the SNOOZE button (9 min) or STOP button.
 *
 * Props:
 *   active     — true when an alarm is currently firing
 *   label      — what the alarm was called ("Wake up", "School run", etc)
 *   hhmm       — the alarm time as "HH:MM" (used in the spoken greeting)
 *   onStop     — called when STOP is pressed (clears pending_alarm)
 *   onSnooze   — called when SNOOZE pressed (creates a one-time alarm for now+9min)
 *   onSpeak    — TTS callback for the wake greeting
 */
export default function AlarmRinger({ active, label, hhmm, onStop, onSnooze, onSpeak }) {
  const audioCtxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);
  const intervalRef = useRef(null);
  const spokenRef = useRef(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!active) {
      stopTone();
      spokenRef.current = false;
      setElapsed(0);
      return undefined;
    }
    startTone();
    // Speak the wake greeting once (TTS doesn't loop)
    if (!spokenRef.current && onSpeak) {
      spokenRef.current = true;
      const niceTime = hhmm || "now";
      onSpeak(`Good morning. It's ${niceTime}. ${label || "Time to wake up"}.`);
    }
    // Elapsed counter so we can show "ringing for 0:34"
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => { clearInterval(id); stopTone(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const startTone = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = 650;
      gain.gain.value = 0.22; // ~25% volume — loud enough to wake, not eardrum-blasting
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      oscRef.current = osc;
      gainRef.current = gain;
      // Alternate pitch every 500ms for that classic clock-radio warble
      let high = false;
      intervalRef.current = setInterval(() => {
        if (!oscRef.current) return;
        high = !high;
        oscRef.current.frequency.setValueAtTime(high ? 920 : 650, ctx.currentTime);
      }, 500);
    } catch (e) {
      // If WebAudio is blocked (most likely autoplay policy without a user
      // gesture), there's nothing we can do silently. The on-screen panel
      // is still visible and the spoken greeting still fires via TTS.
      console.warn("AlarmRinger tone failed:", e);
    }
  };

  const stopTone = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    try { oscRef.current?.stop(); } catch { /* ignore */ }
    oscRef.current = null;
    gainRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    audioCtxRef.current = null;
  };

  if (!active) return null;

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  return (
    <div
      data-testid="alarm-ringer-overlay"
      className="fixed inset-0 z-[2000] flex items-center justify-center"
      style={{
        background: "radial-gradient(circle, rgba(0,0,0,0.92), rgba(0,0,0,0.98))",
        backdropFilter: "blur(8px)",
        animation: "alarm-flash 1s ease-in-out infinite",
      }}
    >
      <style>{`
        @keyframes alarm-flash {
          0%, 100% { background-color: rgba(0,0,0,0.92); }
          50%      { background-color: rgba(80, 0, 0, 0.85); }
        }
        @keyframes alarm-pulse {
          0%, 100% { transform: scale(1); text-shadow: 0 0 30px var(--rc-gold); }
          50%      { transform: scale(1.05); text-shadow: 0 0 60px var(--rc-gold), 0 0 90px rgba(255, 50, 50, 0.6); }
        }
      `}</style>
      <div className="text-center pointer-events-auto">
        <div className="text-[var(--rc-gold)] font-display"
             style={{ fontSize: "clamp(48px, 12vw, 180px)", animation: "alarm-pulse 1s ease-in-out infinite", letterSpacing: "0.1em" }}>
          ⏰ ALARM
        </div>
        <div className="text-[var(--rc-text)] mt-4" style={{ fontSize: "clamp(20px, 3vw, 36px)", letterSpacing: "0.2em", textTransform: "uppercase" }}
             data-testid="alarm-label">
          {label || "Wake up"}
        </div>
        <div className="text-[var(--rc-gold)] mt-2 font-mono opacity-70" data-testid="alarm-hhmm" style={{ fontSize: "clamp(14px, 2vw, 24px)" }}>
          {hhmm || "--:--"} · ringing for {mm}:{ss}
        </div>
        <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center items-center">
          <button
            onClick={onSnooze}
            data-testid="alarm-snooze"
            className="rc-btn"
            style={{ fontSize: "16px", padding: "14px 32px", letterSpacing: "0.16em" }}
          >SNOOZE 9 MIN</button>
          <button
            onClick={onStop}
            data-testid="alarm-stop"
            className="rc-btn"
            style={{ fontSize: "18px", padding: "16px 48px", letterSpacing: "0.16em",
                     background: "var(--rc-gold)", color: "#000" }}
          >STOP ALARM</button>
        </div>
        <div className="mt-8 text-[var(--rc-text-dim)] text-xs opacity-60 max-w-md mx-auto">
          The alarm is also ringing through your speakers. Stop or snooze to silence it. Your bedroom plug has been turned on automatically.
        </div>
      </div>
    </div>
  );
}
